<script setup>
import { ref, reactive, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import CatalogItemsAPI from 'dashboard/api/catalogItems';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import StepWhatsappOtp from './steps/StepWhatsappOtp.vue';
import StepBusinessType from './steps/StepBusinessType.vue';
import StepFunnelLabels from './steps/StepFunnelLabels.vue';
import StepCustomAttributes from './steps/StepCustomAttributes.vue';
import StepQuickReplies from './steps/StepQuickReplies.vue';
import StepFirstProduct from './steps/StepFirstProduct.vue';
import StepPlanComparison from './steps/StepPlanComparison.vue';
import {
  FUNNEL_PRESETS,
  DEFAULT_ATTRIBUTES,
  buildQuickReplies,
} from './constants/segments.js';

const { t, tm } = useI18n();
const store = useStore();
const { currentAccount, updateAccount } = useAccount();
const { isAdmin } = useAdmin();

const TOTAL_STEPS = 7;
const currentStep = ref(1);
const isSaving = ref(false);
const dialogRef = ref(null);

const committed = reactive({
  funnel: false,
  attributes: false,
  replies: false,
});

const wizard = reactive({
  businessType: 'product',
  segmentId: 'outros',
  funnelLabels: FUNNEL_PRESETS.outros.map(label => ({ ...label })),
  attributes: DEFAULT_ATTRIBUTES.map(attribute => ({ ...attribute })),
  pix: {
    pix_key: '',
    pix_key_type: '',
    pix_merchant_name: '',
    pix_merchant_city: '',
  },
  quickReplies: [],
  product: null,
});

const shouldShowQuickSetup = computed(
  () =>
    isAdmin.value &&
    currentAccount.value?.id &&
    currentAccount.value.custom_attributes?.quick_setup_completed === false
);

watch(
  shouldShowQuickSetup,
  shouldShow => {
    nextTick(() => {
      if (shouldShow) dialogRef.value?.open();
      else dialogRef.value?.close();
    });
  },
  { immediate: true }
);

const stepTitle = computed(
  () => tm('QUICK_SETUP.STEPS')[currentStep.value - 1]
);

const slugify = title =>
  title
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\p{L}\p{N}_-]/gu, '');

const commitFunnel = async () => {
  if (committed.funnel) return;
  await Promise.all(
    wizard.funnelLabels.map(label =>
      store.dispatch('labels/create', {
        title: slugify(label.title),
        color: label.color,
        show_on_sidebar: true,
      })
    )
  );
  committed.funnel = true;
};

const commitAttributes = async () => {
  if (committed.attributes) return;
  await Promise.all(
    wizard.attributes.map(attribute =>
      store.dispatch('attributes/create', attribute)
    )
  );
  committed.attributes = true;
};

const commitQuickReplies = async () => {
  await updateAccount(wizard.pix, { silent: true });

  if (committed.replies) return;
  const segment = wizard.segmentId;
  const replies = buildQuickReplies({
    companyName: currentAccount.value?.name,
    segmentLabel: segment !== 'outros' ? segment : '',
    pixKey: wizard.pix.pix_key,
  });
  wizard.quickReplies = replies;
  await Promise.all(
    replies.map(reply => store.dispatch('createCannedResponse', reply))
  );
  committed.replies = true;
};

const commitProduct = async () => {
  const product = wizard.product;
  if (!product || !product.name) return;

  const formData = new FormData();
  formData.append('catalog_item[name]', product.name);
  formData.append('catalog_item[description]', product.description || '');
  formData.append(
    'catalog_item[price_cents]',
    Math.round(Number(product.price || 0) * 100)
  );
  formData.append('catalog_item[currency]', 'BRL');
  if (product.thumbnailFile) {
    formData.append('catalog_item[thumbnail]', product.thumbnailFile);
  }

  await CatalogItemsAPI.create(formData);
};

const finish = async () => {
  await updateAccount({ quick_setup_completed: true }, { silent: true });
  dialogRef.value?.close();
};

const goBack = () => {
  if (currentStep.value > 1) currentStep.value -= 1;
};

const skipSetup = async () => {
  await finish();
};

const goNext = async () => {
  isSaving.value = true;
  try {
    if (currentStep.value === 2) {
      wizard.funnelLabels = (
        FUNNEL_PRESETS[wizard.segmentId] || FUNNEL_PRESETS.outros
      ).map(label => ({ ...label }));
    }
    if (currentStep.value === 3) await commitFunnel();
    if (currentStep.value === 4) await commitAttributes();
    if (currentStep.value === 5) await commitQuickReplies();
    if (currentStep.value === 6) await commitProduct();

    if (currentStep.value === TOTAL_STEPS) {
      await finish();
      return;
    }
    currentStep.value += 1;
  } catch (error) {
    useAlert(t('QUICK_SETUP.ERRORS.GENERIC'));
  } finally {
    isSaving.value = false;
  }
};

const nextLabel = computed(() => {
  if (currentStep.value === TOTAL_STEPS) return t('QUICK_SETUP.FOOTER.FINISH');
  return t('QUICK_SETUP.FOOTER.NEXT');
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    :close-on-click-outside="false"
    :close-on-esc="false"
  >
    <template #description>
      <div class="flex items-center justify-between w-full">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('QUICK_SETUP.TITLE') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{
              `${t('QUICK_SETUP.STEP_OF', { current: currentStep, total: TOTAL_STEPS })} · ${stepTitle}`
            }}
          </p>
        </div>
        <Button
          variant="link"
          slate
          size="sm"
          :label="t('QUICK_SETUP.FOOTER.SKIP_SETUP')"
          @click="skipSetup"
        />
      </div>
    </template>

    <div class="flex flex-col w-full min-h-[320px]">
      <StepWhatsappOtp v-if="currentStep === 1" />
      <StepBusinessType
        v-else-if="currentStep === 2"
        v-model:business-type="wizard.businessType"
        v-model:segment-id="wizard.segmentId"
      />
      <StepFunnelLabels
        v-else-if="currentStep === 3"
        v-model="wizard.funnelLabels"
      />
      <StepCustomAttributes
        v-else-if="currentStep === 4"
        v-model="wizard.attributes"
      />
      <StepQuickReplies
        v-else-if="currentStep === 5"
        v-model:pix="wizard.pix"
        v-model:quick-replies="wizard.quickReplies"
        :segment-id="wizard.segmentId"
        :company-name="currentAccount?.name"
      />
      <StepFirstProduct
        v-else-if="currentStep === 6"
        v-model="wizard.product"
      />
      <StepPlanComparison v-else-if="currentStep === 7" />
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('QUICK_SETUP.FOOTER.BACK')"
          type="button"
          :disabled="currentStep === 1 || isSaving"
          @click="goBack"
        />
        <Button
          color="blue"
          :label="nextLabel"
          type="button"
          :is-loading="isSaving"
          @click="goNext"
        />
      </div>
    </template>
  </Dialog>
</template>
